import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Flag, CheckCircle, Warning, XCircle, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DataTable, FilterBar, FilterChip, StatusBadge, ConfirmModal } from '../../components/admin/AdminComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminReports() {
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionModal, setActionModal] = useState(null);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      const { data } = await axios.get(`${API_URL}/api/admin-console/reports?${params}`, { withCredentials: true });
      setReports(data.reports || []); setTotal(data.total || 0);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchReports(); }, [statusFilter]);

  const resolveReport = async (id, action) => {
    try {
      await axios.put(`${API_URL}/api/admin-console/reports/${id}/resolve`, { action }, { withCredentials: true });
      toast.success(`Report ${action}`); fetchReports();
    } catch { toast.error('Failed to resolve'); }
  };

  const columns = [
    {
      key: 'entity_type', label: 'Type',
      render: v => <span className="text-[12px] text-[#888] capitalize">{v || '—'}</span>,
    },
    { key: 'reason', label: 'Reason', render: v => <span className="text-[13px] text-white">{v}</span> },
    {
      key: 'description', label: 'Description', cellClass: 'hidden md:table-cell max-w-[250px]',
      render: v => <span className="text-[12px] text-[#666] truncate block max-w-[250px]">{v || '—'}</span>,
    },
    { key: 'reporter_name', label: 'Reporter', cellClass: 'hidden lg:table-cell', render: v => <span className="text-[12px] text-[#555]">{v}</span> },
    { key: 'status', label: 'Status', render: v => <StatusBadge status={v} size="xs" /> },
    {
      key: 'created_at', label: 'Date', cellClass: 'hidden lg:table-cell',
      render: v => <span className="text-[11px] text-[#444]">{v ? new Date(v).toLocaleDateString() : '—'}</span>,
    },
    {
      key: 'actions', label: '',
      render: (_, row) => row.status === 'pending' ? (
        <div className="flex gap-1" onClick={e => e.stopPropagation()}>
          <button onClick={() => resolveReport(row.id, 'dismiss')}
            className="text-[10px] px-2 py-1 rounded bg-white/5 text-[#555] hover:text-white" data-testid={`dismiss-${row.id}`}>Dismiss</button>
          <button onClick={() => resolveReport(row.id, 'warn')}
            className="text-[10px] px-2 py-1 rounded bg-[#F59E0B]/10 text-[#F59E0B]" data-testid={`warn-${row.id}`}>Warn</button>
          <button onClick={() => resolveReport(row.id, 'restrict')}
            className="text-[10px] px-2 py-1 rounded bg-[#EF4444]/10 text-[#EF4444]" data-testid={`restrict-${row.id}`}>Restrict</button>
        </div>
      ) : (
        <span className="text-[11px] text-[#444] capitalize">{row.resolution}</span>
      ),
    },
  ];

  return (
    <div data-testid="admin-reports-page">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Reports & Moderation</h1>
        <p className="text-[13px] text-[#444] mt-0.5">{total} reports</p>
      </div>

      <FilterBar searchValue="" onSearchChange={() => {}} searchPlaceholder="—" count={total} label="reports">
        {['', 'pending', 'resolved'].map(s => (
          <FilterChip key={s} label={s || 'All'} active={statusFilter === s}
            onClick={() => setStatusFilter(s)} testId={`filter-status-${s || 'all'}`} />
        ))}
      </FilterBar>

      <DataTable columns={columns} data={reports} loading={loading} emptyMessage="No reports — all clear!" emptyIcon={Flag}
        rowTestIdPrefix="report" />
    </div>
  );
}
